import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';

import Submit from 'bundles/authentication/components/Submit';
import Alert from 'bundles/authentication/shared/Alert';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
import PasswordInput from 'bundles/authentication/shared/PasswordInput';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MODES } from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import thirdPartyAuth from 'bundles/third-party-auth/lib';
import type { SSO_TYPE } from 'bundles/third-party-auth/lib';
import { getErrorMessages } from 'bundles/user-account/utils/errorMessageUtils';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

type Props = {
  className?: string;
  mode: 'existingCourseraAccount' | 'noEmailAvailable' | 'loginDisabledForDomain';
  navigate: (path: MODES, options?: $TSFixMe) => void;
  onSSOSuccess: (isRegistration: boolean) => void;
  referrer?: MODES;
  service?: SSO_TYPE;
  hideBackToLink?: boolean;
  onError?: (error: string | JSX.Element) => void;
  onInvalid?: () => void;
};

type Message = {
  primary: string;
  secondary: string | React.ReactElement<typeof FormattedMessage>;
};

type MessageReturnType = {
  noEmailAvailable: Message;
  existingCourseraAccount: Message;
  loginDisabledForDomain: Message;
};

type State = {
  error?: string;
};

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '24px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '32px 16px',
  },
  description: {
    color: '#3B3B3B',
    fontSize: '14px',
    lineHeight: '24px',
    margin: '0',
    textAlign: 'center',
  },
  form: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  navigation: {
    margin: '0',
    textAlign: 'center',
  },
  section: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'OpenSans',
    fontSize: '24px',
    lineHeight: '30px',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '34px',
      lineHeight: '42px',
    },
  },
  submit: {
    marginTop: '12px',
  },
});

const modeMessages = (): MessageReturnType => ({
  noEmailAvailable: {
    primary: _t('Almost there!'),
    secondary: _t("We'll need your email to complete your account registration."),
  },
  existingCourseraAccount: {
    primary: _t('You already have an \n account with this email.'),
    // TODO make this link about to route locally instead of page refresh
    secondary: (
      <FormattedMessage
        message={_t("Please enter your Coursera password to log in. \n If you don't remember it, {resetPasswordLink}.")}
        resetPasswordLink={
          <a href="/reset" target="resetpw">
            {_t('reset your password')}
          </a>
        }
      />
    ),
  },
  loginDisabledForDomain: {
    primary: _t('Login with Google, Facebook, Apple, jwt are disabled for your email domain.'),
    secondary: _t('Please use Okta instead'),
  },
});

const defaultMessage = () => ({
  primary: _t('An unexpected error occurred.'),
  secondary: _t('Please try again later.'),
});

class CompleteSSO extends React.Component<Props, State> {
  state: State = {};

  setErrorCode = (errorCode?: string) => {
    this.setState({ error: errorCode });
  };

  complete = (values: $TSFixMe) => {
    const { service } = this.props;

    if (!service) {
      return;
    }

    thirdPartyAuth.connect(service, values).then(this.handleSuccess).catch(this.handleError);
  };

  handleError = (error: $TSFixMe) => {
    const { service, onError } = this.props;

    Instrumentation.thirdPartyError(service, error);

    // @ts-expect-error TS7016 TODO: error and code has type any
    this.setState({ error: getErrorMessages[error?.code] });

    if (onError) {
      onError(error);
    }
  };

  handleSuccess = (response: $TSFixMe) => {
    const { service, onSSOSuccess } = this.props;

    Instrumentation.thirdPartyAuth(service, response);

    onSSOSuccess(response.isRegistration);
  };

  navigateBack = () => {
    const { navigate, referrer } = this.props;

    return navigate(referrer || MODES.LOGIN);
  };

  navigateToForgotPassword = () => {
    const { navigate } = this.props;

    navigate(MODES.FORGOT);
  };

  onInvalid = () => {
    const { onInvalid = () => {} } = this.props;

    this.setState({ error: undefined });
    onInvalid();
  };

  render() {
    const { mode, referrer, service, hideBackToLink = false } = this.props;
    const { error } = this.state;

    let message;
    message = modeMessages()[mode] ?? defaultMessage();

    const navigateTo = referrer === MODES.SIGNUP ? _t('Sign Up') : _t('Log in');

    /* Our temporary solution for this scenario (https://coursera.atlassian.net/browse/GR-25354) involves showing a simple error message, with no form input options, to the user.
    We plan to improve this and provide a more permanent solution in the future. */
    const isFacebookEmailMissingScenario = service === 'facebook' && mode === 'noEmailAvailable';
    const facebookEmailMissingOverride = {
      primary: _t('Sorry, we weren’t able to complete your registration.'),
      secondary: _t('Please sign up with email or your Google account.'),
    };

    if (isFacebookEmailMissingScenario) message = facebookEmailMissingOverride;
    /* End */

    return (
      <section {...css('rc-CompleteFormSSO', styles.container)}>
        <h2 {...css(styles.title, styles.section)}>{message.primary}</h2>
        <p {...css(styles.description, styles.section)}>{message.secondary}</p>
        <Form {...css(styles.form, styles.section)} onSubmit={this.complete} onInvalid={this.onInvalid}>
          {error && <Alert type="error">{error}</Alert>}
          {!isFacebookEmailMissingScenario && mode === 'noEmailAvailable' && <EmailInput {...css(styles.input)} />}
          {mode === 'existingCourseraAccount' && (
            <PasswordInput
              {...css(styles.input)}
              hint={<LinkButton onClick={this.navigateToForgotPassword}>{_t('Forgot password?')}</LinkButton>}
              placeholder={_t('Enter your password')}
              autoComplete="current-password"
            />
          )}
          {!isFacebookEmailMissingScenario && ['noEmailAvailable', 'existingCourseraAccount'].includes(mode) && (
            <Submit {...css(styles.submit)}>{_t('Continue')}</Submit>
          )}
        </Form>

        {!hideBackToLink && (
          <p {...css(styles.navigation, styles.section)}>
            <FormattedMessage
              message={_t('Back to {backToLink}.')}
              backToLink={
                <LinkButton onClick={this.navigateBack} type="button">
                  {navigateTo}
                </LinkButton>
              }
            />
          </p>
        )}
      </section>
    );
  }
}

export default CompleteSSO;
